import React, { useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  Printer,
  Download,
  Share2,
  MoreVertical,
} from 'lucide-react';

interface DailyAttendance {
  date: string;
  day: string;
  hours: number;
  courses: string[];
}

interface MonthlyAttendance {
  month: string;
  year: number;
  totalHours: number;
  days: DailyAttendance[];
}

interface TeacherHoursTabProps {
  teacherId: string;
}

const mockMonthlyData: MonthlyAttendance[] = [
  {
    month: 'January',
    year: 2024,
    totalHours: 48,
    days: [
      { date: '01', day: 'Mon', hours: 6, courses: ['Physics 101', 'Chemistry 201'] },
      { date: '02', day: 'Tue', hours: 4, courses: ['Physics 101'] },
      { date: '03', day: 'Wed', hours: 8, courses: ['Physics 101', 'Chemistry 201', 'Biology 101'] },
      { date: '04', day: 'Thu', hours: 6, courses: ['Chemistry 201', 'Biology 101'] },
      { date: '05', day: 'Fri', hours: 4, courses: ['Physics 101'] },
    ],
  },
  {
    month: 'February',
    year: 2024,
    totalHours: 52,
    days: [
      { date: '01', day: 'Thu', hours: 6, courses: ['Physics 101', 'Chemistry 201'] },
      { date: '02', day: 'Fri', hours: 4, courses: ['Physics 101'] },
      { date: '05', day: 'Mon', hours: 8, courses: ['Physics 101', 'Chemistry 201', 'Biology 101'] },
      { date: '06', day: 'Tue', hours: 6, courses: ['Chemistry 201', 'Biology 101'] },
    ],
  },
  {
    month: 'March',
    year: 2024,
    totalHours: 56,
    days: [],
  },
  {
    month: 'April',
    year: 2024,
    totalHours: 44,
    days: [],
  },
];

const yearlyChartData = [
  { month: 'Jan', hours: 48 },
  { month: 'Feb', hours: 52 },
  { month: 'Mar', hours: 56 },
  { month: 'Apr', hours: 44 },
  { month: 'May', hours: 60 },
  { month: 'Jun', hours: 48 },
  { month: 'Jul', hours: 20 },
  { month: 'Aug', hours: 24 },
  { month: 'Sep', hours: 52 },
  { month: 'Oct', hours: 56 },
  { month: 'Nov', hours: 50 },
  { month: 'Dec', hours: 40 },
];

export function TeacherHoursTab({ teacherId }: TeacherHoursTabProps) {
  const [filterPeriod, setFilterPeriod] = useState('Monthly');
  const [expandedMonth, setExpandedMonth] = useState<string | null>('January');
  const [showActionMenu, setShowActionMenu] = useState<string | null>(null);

  const maxHours = Math.max(...yearlyChartData.map((d) => d.hours));

  const handleToggleExpand = (month: string) => {
    setExpandedMonth(expandedMonth === month ? null : month);
  };

  return (
    <div className="space-y-6">
      {/* Yearly Chart */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900">Yearly Hours Overview</h2>
          <div className="flex items-center gap-2">
            <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg">
              <Printer className="w-4 h-4" />
            </button>
            <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg">
              <Download className="w-4 h-4" />
            </button>
            <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg">
              <Share2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Bar Chart */}
        <div className="h-48">
          <div className="flex items-end justify-between h-full gap-2">
            {yearlyChartData.map((data, index) => (
              <div key={data.month} className="flex-1 flex flex-col items-center">
                <div
                  className="w-full bg-teal-500 rounded-t transition-all hover:bg-teal-600"
                  style={{ height: `${(data.hours / maxHours) * 100}%` }}
                  title={`${data.hours} hours`}
                />
                <span className="text-xs text-gray-500 mt-2">{data.month}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-center gap-4 mt-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-teal-500 rounded" />
            <span className="text-sm text-gray-600">Teaching Hours</span>
          </div>
        </div>
      </div>

      {/* Attendance Records */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900">Attendance Records</h2>
          <div className="flex items-center gap-3">
            <select
              value={filterPeriod}
              onChange={(e) => setFilterPeriod(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="Monthly">Monthly</option>
              <option value="Quarterly">Quarterly</option>
              <option value="Yearly">Yearly</option>
            </select>
            <div className="flex items-center gap-1">
              <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg">
                <Printer className="w-4 h-4" />
              </button>
              <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg">
                <Download className="w-4 h-4" />
              </button>
              <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg">
                <Share2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Monthly Records */}
        <div className="space-y-3">
          {mockMonthlyData.map((monthData) => (
            <div key={monthData.month} className="border border-gray-200 rounded-lg overflow-hidden">
              {/* Month Header */}
              <button
                onClick={() => handleToggleExpand(monthData.month)}
                className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <span className="font-medium text-gray-900">
                    {monthData.month} {monthData.year}
                  </span>
                  <span className="text-sm text-gray-500">
                    {monthData.totalHours} hours
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowActionMenu(showActionMenu === monthData.month ? null : monthData.month);
                      }}
                      className="p-1 text-gray-400 hover:text-gray-600"
                    >
                      <MoreVertical className="w-4 h-4" />
                    </button>
                    {showActionMenu === monthData.month && (
                      <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                        <button className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50">
                          Send Message
                        </button>
                        <button className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50">
                          Edit Payment Details
                        </button>
                        <button className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50">
                          Export as PDF
                        </button>
                        <button className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50">
                          Share
                        </button>
                        <button className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50">
                          Send Payroll to User
                        </button>
                        <button className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50">
                          Cancel Payment
                        </button>
                      </div>
                    )}
                  </div>
                  {expandedMonth === monthData.month ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </button>

              {/* Daily Breakdown */}
              {expandedMonth === monthData.month && monthData.days.length > 0 && (
                <div className="p-4 border-t border-gray-200">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Day</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Hours</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Courses</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {monthData.days.map((day, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-sm text-gray-900">{day.date}</td>
                          <td className="px-3 py-2 text-sm text-gray-600">{day.day}</td>
                          <td className="px-3 py-2 text-sm text-gray-900">{day.hours}h</td>
                          <td className="px-3 py-2">
                            <div className="flex flex-wrap gap-1">
                              {day.courses.map((course, i) => (
                                <span
                                  key={i}
                                  className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded"
                                >
                                  {course}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <button className="p-1 text-gray-400 hover:text-gray-600">
                              <MoreVertical className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {expandedMonth === monthData.month && monthData.days.length === 0 && (
                <div className="p-8 text-center text-gray-500">
                  No detailed records available for this month
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Click outside to close action menu */}
      {showActionMenu && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setShowActionMenu(null)}
        />
      )}
    </div>
  );
}
